<!-- The redirect in ngOnInit is asynchronous, so a refused caller would otherwise see the whole
     panel paint for a tick on their way out. -->
@if (canAccessAdmin) {
<section>
    @if (replaceFormInFlight) {
    <aix-loading></aix-loading>
    }
    <aix-header-section [title]="'Admin Actions'"></aix-header-section>
    <aix-notification
        #notificationRef
        [status]="notificationType"
        [canClose]="true"
        [isOpen]="false"
        [classList]="['u-mb16']"
    >
        {{ notificationMessage }}
    </aix-notification>
    <div class="flex-col flex-left-stretch">
        <!-- Move Order Queue (trade-platform#10469). AIX-only, like every card in the block further
             down, and additionally gated on the firm actually HAVING somewhere to move the order
             to: queueOptions holds the firm's signer queues minus the one the order is already in,
             and is empty for a firm whose signer configs carry no branch rules. That is what keeps
             this card off every non-IFG order without naming IFG anywhere.

             Rendered outside that block rather than inside it only because the card belongs at the
             top of the panel; the gate is the same. -->
        @if (!isClientOrderAdminOnly && queueOptions.length) {
        <div class="admin-action-card">
            <div class="admin-action-container">
                <div>
                    <h5 class="u-pb4">Move Order Queue</h5>
                    <p>
                        The order moves to another signing queue and stays in Pending Firm Approval.
                        @if (currentQueueLabel) { It is currently in the
                        <strong>{{ currentQueueLabel }}</strong> queue. }
                    </p>
                </div>

                <div class="u-text-right">
                    <aix-button
                        (click)="toggleMoveQueueOpen()"
                        [data-testing]="'toggleMoveQueueButton'"
                        [isDisabled]="replaceFormInFlight"
                        [buttonType]="buttonTypeLink"
                        [icon]="moveQueueOpen ? 'fa-caret-up' : 'fa-caret-down'"
                    >
                    </aix-button>
                </div>
            </div>
            <div [class.open]="moveQueueOpen" [class.closed]="!moveQueueOpen">
                <div class="admin-action-container u-pt24">
                    <div class="form-selection-container">
                        <h5 class="u-pb4">Select Queue to Move To</h5>
                        <form [formGroup]="moveQueueSelection">
                            <div class="aix-form__group">
                                <!-- [isValid] is not optional on a required aix-dropdown: without it
                                     the control renders permanently invalid, and gets redder once
                                     answered. -->
                                <aix-dropdown
                                    class="standalone-dropdown"
                                    formControlName="configId"
                                    [isRequired]="true"
                                    [isValid]="!!moveQueueSelection.get('configId')?.valid"
                                    labelField="name"
                                    valueField="id"
                                    placeholder="Select Queue"
                                    [data-testing]="'orderQueueDropdown'"
                                    [options]="queueOptions"
                                    (ngModelChange)="selectQueue($event)"
                                >
                                    Queue
                                </aix-dropdown>
                            </div>
                        </form>
                    </div>
                    <div class="u-text-right">
                        <aix-button
                            (click)="onClickUpdateOrder('moveQueue')"
                            [data-testing]="'moveQueueButton'"
                            [isDisabled]="!selectedQueueConfigId || !canMoveQueue"
                            [buttonType]="buttonTypeSecondary"
                            [buttonLabel]="'Move Queue'"
                        >
                        </aix-button>
                        @if (!canMoveQueue) {
                        <p class="sublabel">
                            Order must be in Pending Firm Approval to be moved to another queue.
                        </p>
                        }
                    </div>
                </div>
            </div>
        </div>
        }
        <!-- Edit or Remove Recipient (trade-platform#10428). AIX-only, and rendered only when the
             order has a signer this change could address - recipientOptions excludes receive-only
             rows and anything without a role, so an empty list means there is nothing to act on.

             Unlike Move Order Queue above, the list is NOT what keeps this card off a maintenance
             order: it comes off the order, which a maintenance order has too. supportsRecipientChanges
             is what does, and it belongs in this gate rather than on the submit - a card whose only
             possible state is "inert, for a reason you are not shown" should not be on the page.

             canChangeRecipients is the separate question of whether the SELECTED change can be
             applied right now, and a refusal always has a reason attached. A sent order therefore
             still gets the card: an address can be corrected there, and a removal gets a sublabel
             naming Make Changes - the answer an admin chasing order 40776 needs, where hiding the
             card would leave them hunting for a tool they were told exists.

             The sublabel is not a refusal on its own. With nothing selected on a sent order it
             appears beside an ENABLED button, to name the half of the card that is unavailable
             before the admin picks the half that is not. -->
        @if (!isClientOrderAdminOnly && supportsRecipientChanges && recipientOptions.length) {
        <div class="admin-action-card">
            <div class="admin-action-container">
                <div>
                    <h5 class="u-pb4">Edit or Remove Recipient</h5>
                    <p>
                        Correct a signer's email address, or take them off the envelope entirely.
                        The change is recorded in the order history and is re-applied every time the
                        envelope is rebuilt, so a form cannot add a removed signer back. An address
                        can be corrected after the order has been sent — DocuSign is updated and the
                        signing email re-sent — but removing a recipient still needs Make Changes.
                    </p>
                </div>

                <div class="u-text-right">
                    <aix-button
                        (click)="toggleRecipientChangeOpen()"
                        [data-testing]="'toggleRecipientChangeButton'"
                        [isDisabled]="replaceFormInFlight"
                        [buttonType]="buttonTypeLink"
                        [icon]="recipientChangeOpen ? 'fa-caret-up' : 'fa-caret-down'"
                    >
                    </aix-button>
                </div>
            </div>
            <div [class.open]="recipientChangeOpen" [class.closed]="!recipientChangeOpen">
                <div class="admin-action-container u-pt24">
                    <div class="form-selection-container">
                        <h5 class="u-pb4">Select Recipient and Change</h5>
                        <form [formGroup]="recipientChangeSelection">
                            <div class="aix-form__group">
                                <!-- [isValid] is not optional on a required aix-dropdown: without
                                     it the control renders permanently invalid, and gets redder
                                     once answered. -->
                                <aix-dropdown
                                    class="standalone-dropdown"
                                    formControlName="recipientId"
                                    [isRequired]="true"
                                    [isValid]="
                                        !!recipientChangeSelection.get('recipientId')?.valid
                                    "
                                    labelField="name"
                                    valueField="id"
                                    placeholder="Select Recipient"
                                    [data-testing]="'recipientDropdown'"
                                    [options]="recipientOptions"
                                    (ngModelChange)="selectRecipient($event)"
                                >
                                    Recipient
                                </aix-dropdown>
                            </div>
                            <div class="aix-form__group">
                                <aix-dropdown
                                    class="standalone-dropdown"
                                    formControlName="action"
                                    [isRequired]="true"
                                    [isValid]="!!recipientChangeSelection.get('action')?.valid"
                                    labelField="name"
                                    valueField="id"
                                    placeholder="Select Change"
                                    [data-testing]="'recipientActionDropdown'"
                                    [options]="recipientActions"
                                    (ngModelChange)="selectRecipientAction($event)"
                                >
                                    Change
                                </aix-dropdown>
                            </div>
                            @if (selectedRecipientAction === 'emailChanged') {
                            <div class="aix-form__group">
                                <aix-input
                                    class="aix-form__control--required"
                                    formControlName="newEmail"
                                    placeholder="name@example.com"
                                    [isRequired]="true"
                                    [isValid]="
                                        !!recipientChangeSelection.get('newEmail')?.valid &&
                                        !!newRecipientEmail
                                    "
                                    [data-testing]="'recipientNewEmailInput'"
                                >
                                    New Email Address
                                </aix-input>
                            </div>
                            }
                            <div class="aix-form__group">
                                <!-- Optional, and worth having: the audit comment carries it
                                     verbatim, and "advisor marked this investor as a non-signer" is
                                     what makes the history entry answerable months later. -->
                                <aix-input
                                    formControlName="reason"
                                    placeholder="Why is this changing?"
                                    [isValid]="true"
                                    [data-testing]="'recipientReasonInput'"
                                >
                                    Reason (optional)
                                </aix-input>
                            </div>
                        </form>
                    </div>
                    <div class="u-text-right">
                        <aix-button
                            (click)="onClickUpdateOrder('updateEnvelopeRecipients')"
                            [data-testing]="'updateEnvelopeRecipientsButton'"
                            [isDisabled]="!canSubmitRecipientChange"
                            [buttonType]="buttonTypeSecondary"
                            [buttonLabel]="'Apply Change'"
                        >
                        </aix-button>
                        @if (recipientChangeBlockedReason) {
                        <p class="sublabel">{{ recipientChangeBlockedReason }}</p>
                        }
                    </div>
                </div>
            </div>
        </div>
        }
        <div class="admin-action-card">
            <div class="admin-action-container">
                <div>
                    <h5 class="u-pb4">Replace Form PDF</h5>
                    <p>
                        The uploaded file must already carry whatever signatures this order's stage
                        requires. No signatures are re-gathered. The order will be set to wet-sign.
                    </p>
                </div>

                <div class="u-text-right">
                    <aix-button
                        (click)="toggleReplaceFormOpen()"
                        [data-testing]="'toggleReplaceFormButton'"
                        [isDisabled]="replaceFormInFlight"
                        [buttonType]="buttonTypeLink"
                        [icon]="replaceFormOpen ? 'fa-caret-up' : 'fa-caret-down'"
                    >
                    </aix-button>
                </div>
            </div>
            <div [class.open]="replaceFormOpen" [class.closed]="!replaceFormOpen">
                <div class="admin-action-container u-pt24">
                    <div class="form-selection-container">
                        <h5 class="u-pb4">Select Form to Replace</h5>
                        <form [formGroup]="replaceFormSelection">
                            <div class="aix-form__group">
                                <aix-dropdown
                                    class="standalone-dropdown"
                                    formControlName="formId"
                                    [isRequired]="true"
                                    [isValid]="!!replaceFormSelection.get('formId')?.valid"
                                    labelField="name"
                                    valueField="id"
                                    placeholder="Select Form"
                                    [data-testing]="'replaceFormDropdown'"
                                    [options]="orderForms"
                                    (ngModelChange)="selectReplaceForm($event)"
                                >
                                    Order Form
                                </aix-dropdown>
                            </div>

                            <div class="aix-form__group u-pt16">
                                <h5 class="u-pb4">Replacement PDF</h5>
                                <!-- The native input is the only way to open a file picker, but it is
                            kept out of the layout and driven by an aix-button so the control reads
                            as part of the platform. aix-file-upload is not reusable here: it
                            injects the orders store facade and posts through the order's own
                            supplemental-file flow, not admin-api. -->
                                <input
                                    #replaceFormFileInput
                                    type="file"
                                    accept="application/pdf"
                                    class="replace-form__file-input"
                                    data-testing="replaceFormFileInput"
                                    (change)="onReplaceFormFileSelected($event)"
                                />
                                <aix-button
                                    (click)="replaceFormFileInput.click()"
                                    [data-testing]="'chooseReplaceFormFileButton'"
                                    [isDisabled]="replaceFormInFlight"
                                    [buttonType]="buttonTypeLink"
                                    [buttonLabel]="
                                        replaceFormFile ? 'Choose a different PDF' : 'Choose PDF'
                                    "
                                    [icon]="'fa-upload'"
                                >
                                </aix-button>
                                @if (replaceFormFile) {
                                <p class="sublabel">{{ replaceFormFile.name }}</p>
                                } @else {
                                <p class="sublabel">No file chosen.</p>
                                }
                            </div>
                        </form>
                    </div>
                    <div class="u-text-right">
                        <aix-button
                            (click)="onClickUpdateOrder('replaceForm')"
                            [data-testing]="'replaceFormButton'"
                            [isDisabled]="
                                !canReplaceForm ||
                                !replaceFormId ||
                                !replaceFormFile ||
                                replaceFormInFlight
                            "
                            [buttonType]="buttonTypeSecondary"
                            [buttonLabel]="'Replace Form PDF'"
                        >
                        </aix-button>
                        @if (replaceFormEnvelopeOpen) {
                        <p class="sublabel">{{ envelopeOpenAdvice }}</p>
                        } @else if (!canReplaceForm) {
                        <p class="sublabel">Order is not in the correct status.</p>
                        } @if (replaceFormInFlight) {
                        <p class="sublabel">Uploading. This can take a moment.</p>
                        } @if (replaceFormId && replaceFormFile) {
                        <p class="sublabel">
                            Replacing <strong>{{ selectedFormName }}</strong><br />
                            with <strong>{{ replaceFormFile.name }}</strong>
                        </p>
                        }
                    </div>
                </div>
            </div>
        </div>
        <!-- AIX-only actions. roadmap#333 delegates Replace Form PDF and Mark Order Complete to a
             client tenant's system administrators and nothing else; admin-api refuses the rest for
             them regardless, so offering the cards would only produce a 403.

             Convert Order to Wetsign, the first card in this block, was briefly delegated too and
             product pulled it back after testing in UAT (roadmap#333, comment 5542931994). Moving it
             out again is a product decision, not a tidy-up. -->
        @if (!isClientOrderAdminOnly) {
        <div class="admin-action-card admin-action-container">
            <div>
                <h5 class="u-pb4">Convert Order to Wetsign</h5>
                <p>
                    Subsequent signers will be prompted to download and upload signed documents or,
                    if enabled, to drop signatures in Docusign.
                </p>
            </div>

            <div class="u-text-right">
                <aix-button
                    (click)="onClickUpdateOrder('toWetsign')"
                    [data-testing]="'convertToWetsignButton'"
                    [isDisabled]="isWetsign || !canConvertToWetsign"
                    [buttonType]="buttonTypeSecondary"
                    [buttonLabel]="'Convert to Wetsign'"
                >
                </aix-button>
                @if (isWetsign) {
                <p class="sublabel">Order is already set to wetsign.</p>
                } @if (!canConvertToWetsign) {
                <p class="sublabel">Order is not in the correct status.</p>
                }
            </div>
        </div>
        <div class="admin-action-card">
            <div class="admin-action-container">
                <div>
                    <h5 class="u-pb4">Push Updated Form</h5>
                    <p>
                        The specified form on the order will be replaced with the most recently
                        deployed version.
                    </p>
                </div>

                <div class="u-text-right">
                    <aix-button
                        (click)="toggleUpdateFormOpen()"
                        [data-testing]="'toggleUpdateFormButton'"
                        [isDisabled]="replaceFormInFlight"
                        [buttonType]="buttonTypeLink"
                        [icon]="updateFormOpen ? 'fa-caret-up' : 'fa-caret-down'"
                    >
                    </aix-button>
                </div>
            </div>
            <div [class.open]="updateFormOpen" [class.closed]="!updateFormOpen">
                <div class="admin-action-container u-pt24">
                    <div class="form-selection-container">
                        <h5 class="u-pb4">Select Form to Update</h5>
                        <form [formGroup]="updateFormSelection">
                            <div class="aix-form__group">
                                <aix-dropdown
                                    class="standalone-dropdown"
                                    formControlName="formId"
                                    [isRequired]="true"
                                    [isValid]="!!updateFormSelection.get('formId')?.valid"
                                    labelField="name"
                                    valueField="id"
                                    placeholder="Select Form"
                                    [data-testing]="'orderFormDropdown'"
                                    [options]="orderForms"
                                    (ngModelChange)="selectForm($event)"
                                >
                                    Order Form
                                </aix-dropdown>
                            </div>
                        </form>
                    </div>
                    <div class="u-text-right">
                        <aix-button
                            (click)="onClickUpdateOrder('updateForm')"
                            [data-testing]="'updateFormButton'"
                            [isDisabled]="!selectedFormId"
                            [buttonType]="buttonTypeSecondary"
                            [buttonLabel]="'Update Form'"
                        >
                        </aix-button>
                        @if (selectedFormId) {
                        <p class="sublabel">Update form id {{selectedFormId}}.</p>
                        }
                    </div>
                </div>
            </div>
        </div>
        <div class="admin-action-card admin-action-container">
            <div>
                <h5 class="u-pb4">Change Needs Changes to Needs Clarification</h5>
                <p>
                    The advisor or investor will be able to return the order to the previous review
                    step without voiding signatures.
                </p>
            </div>

            <div class="u-text-right">
                <aix-button
                    (click)="onClickUpdateOrder('toNeedsClarification')"
                    [data-testing]="'nigoToNeedsClarificationButton'"
                    [isDisabled]="status !== 'nigo'"
                    [buttonType]="buttonTypeSecondary"
                    [buttonLabel]="'NIGO to Needs Clarification'"
                >
                </aix-button>
                @if (status === 'needsClarification') {
                <p class="sublabel">Order is already in the Needs Clarification status.</p>
                } @if (status !== 'nigo' && status !== 'needsClarification') {
                <p class="sublabel">Order is not in the Needs Changes status.</p>
                }
            </div>
        </div>
        <div class="admin-action-card admin-action-container">
            <div>
                <h5 class="u-pb4">Move Order Back in Workflow</h5>
                <p>
                    This can only be done if there is a previously completed authorization or
                    signature step in the same status.
                </p>
            </div>

            <div class="u-text-right">
                <aix-button
                    (click)="onClickUpdateOrder('moveBackInWorkflow')"
                    [data-testing]="'moveBackInWorkflowButton'"
                    [isDisabled]="!canMoveBackInWorkflowStatus"
                    [buttonType]="buttonTypeSecondary"
                    [buttonLabel]="'Move Back in Workflow'"
                >
                </aix-button>
                @if (!canMoveBackInWorkflowStatus) {
                <p class="sublabel">Order is not in the correct status.</p>
                } @if (!hasPreviousApproval) {
                <p class="sublabel">Order does not have a previous approval step to restore.</p>
                }
            </div>
        </div>
        <div class="admin-action-card admin-action-container">
            <div>
                <h5 class="u-pb4">Move Order Forward in Workflow</h5>
                <p>
                    This can only be done if the order is on an authorization, signature, or
                    integration step.
                </p>
            </div>

            <div class="u-text-right">
                <aix-button
                    (click)="onClickUpdateOrder('moveForwardInWorkflow')"
                    [data-testing]="'moveForwardInWorkflowButton'"
                    [isDisabled]="!canMoveForwardInWorkflow"
                    [buttonType]="buttonTypeSecondary"
                    [buttonLabel]="'Move Forward in Workflow'"
                >
                </aix-button>
                @if (!canMoveForwardInWorkflow) {
                <p class="sublabel">Order is not in the correct status.</p>
                }
            </div>
        </div>
        }
        <div class="admin-action-card admin-action-container">
            <div>
                <h5 class="u-pb4">Mark Order Complete</h5>
                <p>The order will be marked complete, regardless of the current status.</p>
            </div>

            <div class="u-text-right">
                <aix-button
                    (click)="onClickUpdateOrder('markComplete')"
                    [data-testing]="'markCompleteButton'"
                    [isDisabled]="status === 'completed'"
                    [buttonType]="buttonTypeSecondary"
                    [buttonLabel]="'Mark Complete'"
                >
                </aix-button>
                @if (status === 'completed') {
                <p class="sublabel">Order is already in the Completed status.</p>
                }
            </div>
        </div>
        <!-- AIX-only actions, continued -->
        @if (!isClientOrderAdminOnly) {
        <div class="admin-action-card admin-action-container">
            <div>
                <h5 class="u-pb4">Resubmit Order to Transfer Agent</h5>
                <p>The order will be resubmitted to the transfer agent.</p>
            </div>

            <div class="u-text-right">
                <aix-button
                    (click)="onClickUpdateOrder('resubmit')"
                    [data-testing]="'resubmitButton'"
                    [isDisabled]="status !== 'submitted' && status !== 'offline'"
                    [buttonType]="buttonTypeSecondary"
                    [buttonLabel]="'Resubmit'"
                >
                </aix-button>
                @if (status !== 'submitted' && status !== 'offline') {
                <p class="sublabel">Order is not in a submitted or offline status.</p>
                }
            </div>
        </div>
        <div class="admin-action-card admin-action-container">
            <div>
                <h5 class="u-pb4">Add BCC Email Address to Envelope</h5>
                <p>The admin email will be added to the envelope as a BCC address.</p>
            </div>
            <div class="u-text-right">
                <aix-button
                    (click)="onClickUpdateOrder('addBlindCarbonCopyToEnvelope')"
                    [data-testing]="'addBlindCarbonCopyToEnvelopeButton'"
                    [isDisabled]="!hasValidAddBlindCarbonCopyToEnvelopeState || !hasEnvelopeId"
                    [buttonType]="buttonTypeSecondary"
                    [buttonLabel]="'Add BCC Email Address'"
                >
                </aix-button>
                @if (!hasValidAddBlindCarbonCopyToEnvelopeState) {
                <p class="sublabel">Order is not in the correct status.</p>
                } @if (!hasEnvelopeId) {
                <p class="sublabel">Docusign envelope does not exist for this order.</p>
                }
            </div>
        </div>
        <div class="admin-action-card admin-action-container">
            <div>
                <h5 class="u-pb4">Push Updated Product Profile to Order</h5>
                <p>The order will be updated with most recent product profile data.</p>
            </div>
            <div class="u-text-right">
                <aix-button
                    (click)="onClickUpdateOrder('updateProductProfileData')"
                    [data-testing]="'updateProductProfileDataButton'"
                    [buttonType]="buttonTypeSecondary"
                    [isDisabled]="!canPushProductProfileData"
                    [buttonLabel]="'Push Updated Product Profile Data'"
                >
                </aix-button>
                @if (!canPushProductProfileData) {
                <p class="sublabel">Order is not in the correct status.</p>
                }
            </div>
        </div>

        <div class="admin-action-card">
            <div class="admin-action-container">
                <div>
                    <h5 class="u-pb4">Resubmit File to IFG Gateway</h5>
                    <p>
                        The specified file on the order will be resubmitted for IFG Gateway
                        integration.
                    </p>
                </div>
                <div class="u-text-right">
                    <aix-button
                        (click)="toggleResubmitFileOpen()"
                        [data-testing]="'toggleResubmitFileButton'"
                        [isDisabled]="replaceFormInFlight"
                        [buttonType]="buttonTypeLink"
                        [icon]="resubmitFileOpen ? 'fa-caret-up' : 'fa-caret-down'"
                    >
                    </aix-button>
                </div>
            </div>
            <div [class.open]="resubmitFileOpen" [class.closed]="!resubmitFileOpen">
                <div class="admin-action-container u-pt24">
                    <div class="form-selection-container">
                        <h5 class="u-pb4">Select File to Resubmit</h5>
                        <form [formGroup]="resubmitFileSelection">
                            <div class="aix-form__group">
                                <aix-dropdown
                                    class="standalone-dropdown"
                                    formControlName="fileTransmissionId"
                                    [isRequired]="true"
                                    [isValid]="!!resubmitFileSelection.get('fileTransmissionId')?.valid"
                                    labelField="displayName"
                                    valueField="id"
                                    placeholder="Select File"
                                    [data-testing]="'orderFileDropdown'"
                                    [options]="ifgGatewayFiles"
                                    (ngModelChange)="selectFile($event)"
                                >
                                    Order File
                                </aix-dropdown>
                            </div>
                        </form>
                    </div>
                    <div class="u-text-right">
                        <aix-button
                            (click)="onClickUpdateOrder('resubmitFileToIfgGateway')"
                            [data-testing]="'updateFileButton'"
                            [isDisabled]="!selectedFileTransmissionId || !canResubmitFileToIfgGateway || !isIfgAdvisorOrder"
                            [buttonType]="buttonTypeSecondary"
                            [buttonLabel]="'Resubmit File'"
                        >
                        </aix-button>
                        <aix-button
                            (click)="onClickUpdateOrder('regenerateFileList')"
                            [data-testing]="'regenerateButton'"
                            [buttonType]="buttonTypeSecondary"
                            [buttonLabel]="'Regenerate File List'"
                        >
                        </aix-button>
                        @if (selectedFileTransmissionId) {
                        <p class="sublabel">Resubmit file ID: {{selectedFileTransmissionId}}.</p>
                        } @if (!canResubmitFileToIfgGateway) {
                        <p class="sublabel">Order is not in the correct status.</p>
                        } @if (!isIfgAdvisorOrder) {
                        <p class="sublabel">Order is not eligible for IFG submission.</p>
                        }
                    </div>
                </div>
            </div>
        </div>

        <div class="admin-action-card">
            <div class="admin-action-container">
                <div>
                    <h5 class="u-pb4">Resubmit Integration</h5>
                    <p>The order will be re-sent to the selected integration partner.</p>
                </div>
                <div class="u-text-right">
                    <aix-button
                        (click)="toggleResubmitIntegrationOpen()"
                        [data-testing]="'toggleResubmitIntegrationButton'"
                        [isDisabled]="replaceFormInFlight"
                        [buttonType]="buttonTypeLink"
                        [icon]="resubmitIntegrationOpen ? 'fa-caret-up' : 'fa-caret-down'"
                    >
                    </aix-button>
                </div>
            </div>
            <div [class.open]="resubmitIntegrationOpen" [class.closed]="!resubmitIntegrationOpen">
                <div class="admin-action-container u-pt24">
                    <div class="form-selection-container">
                        <h5 class="u-pb4">Select Integration Partner</h5>
                        <form [formGroup]="resubmitIntegrationSelection">
                            <div class="aix-form__group">
                                <aix-dropdown
                                    class="standalone-dropdown"
                                    formControlName="integrationPartnerId"
                                    [isRequired]="true"
                                    [isValid]="!!resubmitIntegrationSelection.get('integrationPartnerId')?.valid"
                                    labelField="name"
                                    valueField="id"
                                    placeholder="Select Integration Partner"
                                    [data-testing]="'integrationPartnerDropdown'"
                                    [options]="integrationPartners"
                                    (ngModelChange)="selectIntegrationPartner($event)"
                                >
                                    Integration Partner
                                </aix-dropdown>
                            </div>
                        </form>
                    </div>
                    <div class="u-text-right">
                        <aix-button
                            (click)="onClickUpdateOrder('resubmitOrderToIfgGateway')"
                            [data-testing]="'resubmitOrderToIfgGatewayButton'"
                            [isDisabled]="!selectedIntegrationPartner || (selectedIntegrationPartner === 'gateway' && (!isIfgAdvisorOrder || !integrationApprovalConfig))"
                            [buttonType]="buttonTypeSecondary"
                            [buttonLabel]="'Resubmit Integration'"
                        >
                        </aix-button>
                        @if (selectedIntegrationPartner) {
                        <p class="sublabel">Resubmit to {{selectedIntegrationPartner}}.</p>
                        } @if (selectedIntegrationPartner === 'gateway' && !isIfgAdvisorOrder) {
                        <p class="sublabel">Order is not eligible for Gateway submission.</p>
                        } @if (selectedIntegrationPartner === 'gateway' && isIfgAdvisorOrder &&
                        !integrationApprovalConfig) {
                        <p class="sublabel">Order does not have an integration approval step.</p>
                        }
                    </div>
                </div>
            </div>
        </div>
        }
    </div>
</section>
}
<aix-modal
    #confirmationModal
    modalType="confirmation"
    [modalTitle]="modalText.title"
    (optionSelected)="confirmationModalSelected($event)"
>
    <ng-template #confirmationTemplate>
        <p>{{ modalText.text }}</p>
        @if (modalText.detail) {
        <p class="u-mt14">{{ modalText.detail }}</p>
        }
    </ng-template>
</aix-modal>
